import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import type { Control } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Select } from '@/components/common/select';
import { Drawer } from '@/components/feedback/drawer';
import { FormField, MultiSelectField } from '@/components/forms';
import { onlyEnabled } from '@/config/preview-features';
import { appTzLabel, fromDateRange, toDayjsRange } from '@/lib/date';

import { DEVICE_TYPE_OPTIONS, SUBSCRIPTION_STATUS_CHIP } from '../format';
import { customerSubscriptionFiltersSchema } from '../schemas';
import {
  customerSubscriptionToDefaults,
  customerSubscriptionToFilters,
} from '../subscription-filters';
import type { CustomerSubscriptionFilterFormValues, CustomerSubscriptionFilters } from '../types';

import styles from './customer-subscription-filter-drawer.module.scss';

const SUBSCRIPTION_STATUS_OPTIONS = (
  Object.keys(SUBSCRIPTION_STATUS_CHIP) as (keyof typeof SUBSCRIPTION_STATUS_CHIP)[]
).map((value) => ({ value, label: SUBSCRIPTION_STATUS_CHIP[value].label }));

type Option = { value: string; label: ReactNode };

type FilterSelectName = 'subscriptionStatus' | 'deviceType';

type FilterSelectProps = {
  name: FilterSelectName;
  control: Control<CustomerSubscriptionFilterFormValues>;
  label: string;
  options: Option[];
};

/** Single-select filter field that clears to `null` rather than `undefined` (see
 *  payment-filter-drawer for the full rationale — RHF treats `undefined` as "reset to default",
 *  which would snap a re-seeded select back on clear). */
function FilterSelect({ name, control, label, options }: FilterSelectProps) {
  return (
    <FormField name={name} control={control} label={label}>
      {({ field, fieldState }) => (
        <Select
          id={name}
          style={{ width: '100%' }}
          placeholder="Select"
          allowClear
          options={options}
          status={fieldState.error ? 'error' : undefined}
          value={field.value ?? undefined}
          onChange={(next) => field.onChange(next ?? null)}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}

export type CustomerSubscriptionFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: CustomerSubscriptionFilters;
  /** Distinct {id, name} device pairs from the currently-loaded page (no device-name meta
   *  endpoint exists). */
  deviceOptions: { value: string; label: string }[];
  onApply: (filters: CustomerSubscriptionFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<CustomerSubscriptionFilterDrawerProps, 'open'>;

function FilterBody({ value, deviceOptions, onApply, onClose }: BodyProps) {
  const draft = customerSubscriptionToDefaults(value);
  const form = useForm<CustomerSubscriptionFilterFormValues>({
    resolver: zodResolver(customerSubscriptionFiltersSchema),
    defaultValues: {
      ...draft,
      deviceId: draft.deviceId ?? [],
      startDateFrom: draft.startDateFrom ?? null,
      startDateTo: draft.startDateTo ?? null,
      endDateFrom: draft.endDateFrom ?? null,
      endDateTo: draft.endDateTo ?? null,
    },
    mode: 'onBlur',
  });

  const [startFrom, startTo, endFrom, endTo] = useWatch({
    control: form.control,
    name: ['startDateFrom', 'startDateTo', 'endDateFrom', 'endDateTo'],
  });
  const startError = form.formState.errors.startDateTo?.message;
  const endError = form.formState.errors.endDateTo?.message;

  const handleApply = form.handleSubmit((values) => {
    onApply(customerSubscriptionToFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={Boolean(startError || endError)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="deviceId"
        control={form.control}
        label="Device"
        placeholder="Select"
        options={deviceOptions}
      />
      <FilterSelect
        name="subscriptionStatus"
        control={form.control}
        label="Subscription Status"
        options={SUBSCRIPTION_STATUS_OPTIONS}
      />
      <FilterSelect
        name="deviceType"
        control={form.control}
        label="Device Type"
        options={onlyEnabled(DEVICE_TYPE_OPTIONS)}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Subscription Start Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Subscription Start Date Range')}
          format="MM/DD/YYYY"
          allowEmpty={[true, true]}
          style={{ width: '100%' }}
          status={startError ? 'error' : undefined}
          value={toDayjsRange(startFrom && startTo ? [startFrom, startTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('startDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('startDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {startError ? <p className={styles.error}>{startError}</p> : null}
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Subscription End Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Subscription End Date Range')}
          format="MM/DD/YYYY"
          allowEmpty={[true, true]}
          style={{ width: '100%' }}
          status={endError ? 'error' : undefined}
          value={toDayjsRange(endFrom && endTo ? [endFrom, endTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('endDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('endDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {endError ? <p className={styles.error}>{endError}</p> : null}
      </fieldset>
    </Drawer>
  );
}

export function CustomerSubscriptionFilterDrawer({
  open,
  ...rest
}: CustomerSubscriptionFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
